import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import { Modal, Button, Form, InputGroup, FormControl } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,  
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [reportFile, setReportFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingReport, setExistingReport] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusForm, setStatusForm] = useState({
    appoint_id: "",
    appoint_status: 0,
    appoint_notes: "",
    appoint_report: null,
    appoint_followup: false,
    appoint_followupdate: "",
    appoint_followuptimefrom: "",
    appoint_followuptimeto: "",
    appointrej_report: "",
  });
  const accesstoken = localStorage.getItem("token");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalData, setApprovalData] = useState(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [existingRejectionInfo, setExistingRejectionInfo] = useState(null);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [reportViewerUrl, setReportViewerUrl] = useState(null);
  const [reportViewerType, setReportViewerType] = useState(null);
  const [reportViewerLoading, setReportViewerLoading] = useState(false);
  const roleId = Number(localStorage.getItem("roleid"));

  const isManager = roleId === 3 || roleId === 4;

  const navigate = useNavigate();

  const getAllAppointments = () => {
    axios
      .get(baseurladdress + "appointment/getallappointments", {
        headers: {
          Authorization: `Bearer ${accesstoken}`,
        },
      })
      .then((res) => {
        if (res.data.status === "Success") {
          setAppointments(res.data.appointments);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    getAllAppointments();
  }, []);


  function openApprovalModal(appointment) {
    setApprovalData(null);
    setRejectionNote("");

    axios
      .get(baseurladdress + `appointment/getreport/${appointment.appoint_id}`, {
        headers: { Authorization: `Bearer ${accesstoken}` },
      })
      .then((res) => {
        setApprovalData({
          ...appointment,
          report: res.data.report,
          rejected_report: res.data.rejected_report || null,
          rejection_note: res.data.rejection_note || "",
          rejected_on: res.data.rejected_on || null,
        });
        setShowApprovalModal(true);
      });
  }

  function approveAppointment(appointmentId) {
    const roleid = localStorage.getItem("roleid");
    axios
      .post(
        baseurladdress + "appointment/approveappointment",
        {
          appoint_id: appointmentId,
          roleid: roleid,
        },
        {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        if (res.data.status === "success") {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Appointment Approved Successfully",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });
          setShowApprovalModal(false);
          getAllAppointments();
        } else {
          Swal.fire({
            icon: "error",
            title: "Approval Failed",
            text: res.data.message || "Unable to approve appointment.",
          });
        }
      })
      .catch((error) => {
        console.error("Approval error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong while approving the appointment.",
        });
      });
  }


  function deleteAppointment(appoint_id) {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(baseurladdress + `appointment/deleteappointment/${appoint_id}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: `Bearer ${accesstoken}`,
          },
        })
          .then((response) => response.json())
          .then((result) => {
            if (result.status === "success") {
              Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: "Deleted Successfully",
                showConfirmButton: false,
                timer: 1200,
                timerProgressBar: true,
              });
              getAllAppointments();
            } else if (result.status === "Not Found") {
              Swal.fire({
                icon: "error",
                title: "Not Found",
                text: "Appointment not found or already deleted.",
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Failed",
                text: "Unable to delete appointment. Please try again.",
              });
            }
          })
          .catch((error) => {
            console.error("Delete appointment error:", error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Something went wrong while deleting appointment.",
            });
          });
      }
    });
  }

  function openReportUploadModal(appointment) {
    setSelectedAppointment(appointment);
    setReportFile(null);
    setExistingReport(null);
    setExistingRejectionInfo(null);

    axios
      .get(baseurladdress + `appointment/getreport/${appointment.appoint_id}`, {
        headers: {
          Authorization: `Bearer ${accesstoken}`,
        },
      })
      .then((res) => {
        if (res.data.status === "success" && res.data.report) {
          setExistingReport(res.data.report);
        }
        if (appointment.appointapproval_status === 2) {
          setExistingRejectionInfo({
            rejection_note: res.data.rejection_note || "",
            rejected_on: res.data.rejected_on || null,
            rejected_report: res.data.rejected_report || null,
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching existing report:", err);
      });

    setShowReportModal(true);
  }

  const appointmentStatusOptions = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Arrived Site" },
    { value: 2, label: "In Progress" },
    { value: 3, label: "Completed" },
    { value: 4, label: "Cancelled" },
  ];

  function openStatusUpdateModal(appointment) {
    setSelectedAppointment(appointment);
    setStatusForm({
      appoint_id: appointment.appoint_id,
      appoint_status: Number(appointment.appoint_status ?? 0),
      appoint_notes: appointment.appoint_notes || "",
      appoint_report: null,
      appoint_followup: Boolean(
        appointment.appoint_followupdate ||
        appointment.appoint_followuptimefrom ||
        appointment.appoint_followuptimeto
      ),
      appoint_followupdate: appointment.appoint_followupdate
        ? String(appointment.appoint_followupdate).slice(0, 10)
        : "",
      appoint_followuptimefrom: appointment.appoint_followuptimefrom || "",
      appoint_followuptimeto: appointment.appoint_followuptimeto || "",
      appointrej_report: appointment.appointrej_report || "",
    });
    setShowStatusModal(true);
  }

  function handleStatusFormChange(field, value) {
    setStatusForm((prev) => ({ ...prev, [field]: value }));
  }

  function submitStatusUpdate() {
    const appointmentId = selectedAppointment?.appoint_id;
    const statusValue = Number(statusForm.appoint_status);

    if (!appointmentId) {
      Swal.fire({ icon: "error", title: "Appointment not found" });
      return;
    }

    if (statusValue === 4 && !statusForm.appointrej_report.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Cancelled appointment requires a rejection note",
        text: "Please enter a reason in the cancel report field.",
      });
      return;
    }

    if (statusValue === 3 && statusForm.appoint_followup && !statusForm.appoint_followupdate) {
      Swal.fire({
        icon: "warning",
        title: "Follow-up date required",
        text: "Please select a follow-up date when the follow-up option is enabled.",
      });
      return;
    }

    setStatusSubmitting(true);

    const payload = {
      appoint_id: appointmentId,
      appoint_status: statusValue,
      appoint_notes: statusForm.appoint_notes || "",
      appoint_followup: statusForm.appoint_followup ? 1 : 0,
      appoint_followupdate: statusForm.appoint_followup ? statusForm.appoint_followupdate || "" : "",
      appoint_followuptimefrom: statusForm.appoint_followup ? statusForm.appoint_followuptimefrom || "" : "",
      appoint_followuptimeto: statusForm.appoint_followup ? statusForm.appoint_followuptimeto || "" : "",
      appointrej_report: statusValue === 4 ? statusForm.appointrej_report.trim() : "",
    };

    const isCompleteWithFile = statusValue === 3 && statusForm.appoint_report;
    const submitUrl = baseurladdress + "appointment/updateappointmentstatus";

    const request = isCompleteWithFile
      ? (() => {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formData.append(key, value);
            }
          });
          formData.append("appoint_report", statusForm.appoint_report);
          return axios.post(submitUrl, formData, {
            headers: {
              Authorization: `Bearer ${accesstoken}`,
              "Content-Type": "multipart/form-data",
            },
          });
        })()
      : axios.post(submitUrl, payload, {
          headers: {
            Authorization: `Bearer ${accesstoken}`,
            "Content-Type": "application/json",
          },
        });

    request
      .then((res) => {
        const success = res?.data?.status === "success" || res?.data?.status === "Success" || res?.data?.status === "updated" || res?.status === 200;
        if (success) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: "Appointment status updated",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });
          setShowStatusModal(false);
          setStatusSubmitting(false);
          getAllAppointments();
        } else {
          Swal.fire({
            icon: "error",
            title: "Update Failed",
            text: res?.data?.message || "Unable to update appointment status.",
          });
          setStatusSubmitting(false);
        }
      })
      .catch((error) => {
        console.error("Appointment status update error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong while updating appointment status.",
        });
        setStatusSubmitting(false);
      });
  }

  function handleReportFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: "error",
          title: "Invalid File Type",
          text: "Please upload PDF, DOC, DOCX, or image files only.",
        });
        e.target.value = "";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "File size should not exceed 10MB.",
        });
        e.target.value = "";
        return;
      }

      setReportFile(file);
    }
  }

  function handleReportUpload() {
    if (existingReport && reportFile) {
      Swal.fire({
        title: "Replace Existing Report?",
        text: "This will replace the previously uploaded report. Continue?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, replace it!",
      }).then((result) => {
        if (result.isConfirmed) {
          uploadReport();
        }
      });
    } else {
      uploadReport();
    }
  }

  function uploadReport() {
    if (!reportFile) {
      Swal.fire({
        icon: "warning",
        title: "No file selected",
        text: "Please choose a file before uploading",
        confirmButtonText: "OK",
      });
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("report", reportFile);
    formData.append("appoint_id", selectedAppointment.appoint_id);

    axios
      .post(baseurladdress + "appointment/uploadreport", formData, {
        headers: {
          Authorization: `Bearer ${accesstoken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        setUploading(false);

        if (res.data.status === "success") {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "success",
            title: existingReport
              ? "Report Replaced Successfully"
              : "Report Uploaded Successfully",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
          });

          setShowReportModal(false);
          setReportFile(null);
          setExistingReport(null);
          getAllAppointments();
        } else {
          Swal.fire({
            icon: "error",
            title: "Update Failed",
            text: res.data.message || "Unable to update report. Please try again.",
          });
        }
      })
      .catch((error) => {
        setUploading(false);
        console.error("Report upload error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong while updating the report.",
        });
      });
  }


  function viewReport(appointmentId) {
    setReportViewerLoading(true);
    setReportViewerUrl(null);
    setReportViewerType(null);
    setShowReportViewer(true);

    axios
      .get(baseurladdress + `appointment/downloadreport/${appointmentId}`, {
        headers: { Authorization: `Bearer ${accesstoken}` },
        responseType: "blob",
      })
      .then((res) => {
        const contentType = res.headers["content-type"];
        const blob = new Blob([res.data], { type: contentType });
        if (contentType?.includes("pdf")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setReportViewerUrl(reader.result);
            setReportViewerType(contentType);
            setReportViewerLoading(false);
          };
          reader.readAsDataURL(blob);
        } else {
          setReportViewerUrl(URL.createObjectURL(blob));
          setReportViewerType(contentType);
          setReportViewerLoading(false);
        }
      })
      .catch((err) => {
        console.error("View report error:", err);
        setShowReportViewer(false);
        setReportViewerLoading(false);
        Swal.fire("Error", "Unable to load report", "error");
      });
  }

  function closeReportViewer() {
    if (reportViewerUrl) URL.revokeObjectURL(reportViewerUrl);
    setReportViewerUrl(null);
    setReportViewerType(null);
    setShowReportViewer(false);
  }

  function viewRejectedReport(appointmentId) {
    setReportViewerLoading(true);
    setReportViewerUrl(null);
    setReportViewerType(null);
    setShowReportViewer(true);

    axios
      .get(baseurladdress + `appointment/viewrejectedreport/${appointmentId}`, {
        headers: { Authorization: `Bearer ${accesstoken}` },
        responseType: "blob",
      })
      .then((res) => {
        const contentType = res.headers["content-type"];
        const blob = new Blob([res.data], { type: contentType });
        if (contentType?.includes("pdf")) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setReportViewerUrl(reader.result);
            setReportViewerType(contentType);
            setReportViewerLoading(false);
          };
          reader.readAsDataURL(blob);
        } else {
          setReportViewerUrl(URL.createObjectURL(blob));
          setReportViewerType(contentType);
          setReportViewerLoading(false);
        }
      })
      .catch((err) => {
        console.error("View rejected report error:", err);
        setShowReportViewer(false);
        setReportViewerLoading(false);
        Swal.fire("Error", "Unable to load rejected report", "error");
      });
  }

  function AddAppoint() {
    navigate("/addappoint");
  }

  function EditAppoint(appoint_id) {
    navigate("/editappoint/" + appoint_id);
  }

  // Define table columns
  const columns = useMemo(
    () => [
      {
        header: "Sl.No",
        accessorFn: (row, index) => index + 1,
        id: "slNo",
        size: 60,
      },
      {
        header: "Client Name",
        accessorKey: "appoint_clientname",
        size: 150,
      },
      {
        header: "Client Type",
        accessorKey: "appoint_clienttype",
        cell: (info) => (info.getValue() === 0 ? "Doctor" : "Hospital"),
        size: 100,
      },
      {
        header: "Appointment Date",
        accessorKey: "appoint_appointmentdate",
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
        size: 120,
      },
      {
        header: "Assigned To",
        accessorKey: "appoint_staffname",
        size: 130,
      },

      {
        header: "Status Update",
        id: "upload",
        cell: (info) => {
          const isApproved = info.row.original.appointapproval_status === 1;
          return (
            <Button
              variant="link"
              className="p-0 text-primary"
              onClick={(e) => {
                e.currentTarget.blur();
                openStatusUpdateModal(info.row.original);
              }}
              title={
                isApproved
                  ? "Appointment approved - view status details"
                  : "Update Appointment Status"
              }
              disabled={isApproved}
            >
              <Icon.Upload />
            </Button>
          );
        },
        size: 110,
      },
      {
        header: "Approval",
        id: "approval",
        cell: (info) => {
          const status = info.row.original.appointapproval_status;

          if (status === 1) {
            return (
              <span className="badge bg-success">
                <Icon.CheckCircleFill className="me-1" />
                Approved
              </span>
            );
          }

          if (status === 2) {
            return (
              <span className="badge bg-danger">
                <Icon.XCircleFill className="me-1" />
                Rejected
              </span>
            );
          }

          // Pending
          return (
            <Button
              size="sm"
              variant="outline-success"
              onClick={(e) => {
                openApprovalModal(info.row.original);
                e.currentTarget.blur();

              }}
            >
              <Icon.Check2Circle className="me-1" />
              Review
            </Button>
          );
        },
      }
      ,

      {
        header: "Actions",
        id: "actions",
        cell: (info) => {
          const isApproved = info.row.original.appointapproval_status === 1;

          return (
            <div className="d-flex gap-2">
              {/* Edit */}
              <Icon.PencilSquare
                className={isApproved ? "text-muted" : "text-primary"}
                onClick={() => {
                  if (!isApproved) {
                    EditAppoint(info.row.original.appoint_id);
                  }
                }}
                style={{ cursor: isApproved ? "not-allowed" : "pointer" }}
                title={
                  isApproved
                    ? "Cannot edit - Appointment approved"
                    : "Edit"
                }
              />

              {/* Delete */}
              <Icon.Trash
                className={isApproved ? "text-muted" : "text-danger"}
                onClick={() => {
                  if (!isApproved) {
                    deleteAppointment(info.row.original.appoint_id);
                  }
                }}
                style={{ cursor: isApproved ? "not-allowed" : "pointer" }}
                title={
                  isApproved
                    ? "Cannot delete - Appointment approved"
                    : "Delete"
                }
              />
            </div>
          );
        },
        size: 80,
      }

    ],
    []
  );

  const table = useReactTable({
    data: appointments,
    columns,
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <div className="container-fluid px-2 px-sm-4 py-3">
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
          <h6 className="mb-0">
            APPOINTMENT DETAILS
          </h6>
          <Button
            variant="light"
            size="sm"
            onClick={(e) => {
              AddAppoint();
              e.currentTarget.blur();
            }}
          >
            <Icon.Plus className="me-1" />
            Add
          </Button>
        </div>

        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show</span>
              <Form.Select
                size="sm"
                style={{ width: "70px" }}
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              >
                {[5, 10, 20, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </Form.Select>
              <span className="text-muted small">entries</span>
            </div>

            <InputGroup style={{ width: "min(100%, 250px)" }}>
              <InputGroup.Text>
                <Icon.Search size={14} />
              </InputGroup.Text>
              <FormControl
                size="sm"
                placeholder="Search..."
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </InputGroup>
          </div>

         
          <div className="table-responsive">
            <table className="table table-hover table-sm align-middle">
              <thead className="table-light">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="text-nowrap"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none d-flex align-items-center"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort() && (
                              <span className="ms-1">
                                {{
                                  asc: <Icon.ChevronUp size={12} />,
                                  desc: <Icon.ChevronDown size={12} />,
                                }[header.column.getIsSorted()] ?? (
                                    <Icon.ChevronExpand size={12} className="text-muted" />
                                  )}
                              </span>
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted py-4">
                      No appointments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Compact Pagination */}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
            <div className="text-muted small">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{" "}
              of {table.getFilteredRowModel().rows.length} entries
            </div>

            <div className="btn-group btn-group-sm">
              <Button
                variant="outline-secondary"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon.ChevronDoubleLeft size={14} />
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon.ChevronLeft size={14} />
              </Button>
              <Button variant="outline-secondary" disabled>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <Icon.ChevronRight size={14} />
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <Icon.ChevronDoubleRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Upload Modal */}
      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        centered
        className="report-upload-modal"
        size="md"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3 d-none d-sm-flex">
              <Icon.CloudUpload size={24} className="text-primary" />
            </div>
            <span className="fs-5 fs-sm-4">
              {existingReport ? "Update Report" : "Upload Report"}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-3 px-sm-4">
          {selectedAppointment && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-3">
                <h6 className="text-uppercase text-muted mb-3 d-flex align-items-center"
                  style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <Icon.InfoCircle className="me-2" size={14} />
                  Appointment Details
                </h6>

                {/* Responsive Grid Layout */}
                <div className="row g-2 g-sm-3">
                  <div className="col-12 col-sm-6">
                    <div className="d-flex align-items-start">
                      <Icon.Person className="me-2 text-primary mt-1 flex-shrink-0" size={16} />
                      <div className="flex-grow-1 min-w-0">
                        <small className="text-muted d-block">Client</small>
                        <strong className="d-block text-truncate">{selectedAppointment.appoint_clientname}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6">
                    <div className="d-flex align-items-start">
                      <Icon.PersonBadge className="me-2 text-primary mt-1 flex-shrink-0" size={16} />
                      <div className="flex-grow-1 min-w-0">
                        <small className="text-muted d-block">Staff</small>
                        <strong className="d-block text-truncate">{selectedAppointment.appoint_staffname}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="d-flex align-items-start">
                      <Icon.CalendarCheck className="me-2 text-primary mt-1 flex-shrink-0" size={16} />
                      <div className="flex-grow-1">
                        <small className="text-muted d-block">Appointment Date</small>
                        <strong className="d-block">
                          {new Date(selectedAppointment.appoint_appointmentdate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Banner */}
          {selectedAppointment?.appointapproval_status === 2 && (
            <div className="alert alert-danger border-start border-danger border-4 mb-3 p-0 overflow-hidden">
              <div className="d-flex align-items-center bg-danger bg-opacity-10 px-3 py-2 border-bottom border-danger border-opacity-25">
                <Icon.XCircleFill className="me-2 text-danger flex-shrink-0" size={18} />
                <strong className="text-danger">Report Rejected by Manager</strong>
              </div>
              <div className="px-3 py-2">
                {existingRejectionInfo?.rejection_note ? (
                  <>
                    <p className="mb-2 fw-semibold" style={{ fontSize: '0.9rem' }}>
                      <span className="text-muted">Rejection Reason: </span>
                      {existingRejectionInfo.rejection_note}
                    </p>
                  </>
                ) : (
                  <p className="mb-2 text-muted" style={{ fontSize: '0.9rem' }}>
                    No specific reason provided by the manager.
                  </p>
                )}
                {existingRejectionInfo?.rejected_on && (
                  <small className="text-muted">
                    <Icon.ClockHistory className="me-1" size={13} />
                    Rejected on: {new Date(existingRejectionInfo.rejected_on).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </small>
                )}
                {existingRejectionInfo?.rejected_report && (
                  <div className="mt-2 pt-2 border-top border-danger border-opacity-25">
                    <div className="d-flex align-items-center">
                      <small className="text-muted me-2 mb-0">Rejected Report File:</small>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          viewRejectedReport(selectedAppointment.appoint_id);
                        }}
                        className="d-flex align-items-center"
                      >
                        <Icon.Eye className="me-1" size={14} />
                        View Rejected Report
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {existingReport && selectedAppointment?.appointapproval_status !== 2 && (
            <div className="alert alert-warning border-0 mb-3 d-flex align-items-start">
              <Icon.ExclamationTriangle className="me-2 mt-1 flex-shrink-0" size={20} />
              <div className="flex-grow-1 min-w-0">
                <strong className="d-block mb-1">Current Report</strong>
                <div className="text-truncate small mb-1">{existingReport.filename || "Report uploaded"}</div>
                <small className="text-muted d-block">Uploading a new file will replace the existing report.</small>
              </div>
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center fw-semibold">
                <Icon.FileEarmarkArrowUp className="me-2" size={10} />
                {existingReport ? "Select New Report File" : "Select Report File"}
                {!existingReport && <span className="text-danger ms-1">*</span>}
              </Form.Label>

              <Form.Control
                type="file"
                onChange={handleReportFileChange}
                className="form-control-lg"
                style={{ cursor: 'pointer' }}
              />

              <Form.Text className="text-muted d-block mt-2">
                <div className="d-flex flex-column flex-sm-row align-items-start">
                  <span className="me-sm-3 mb-1 mb-sm-0">
                    <Icon.FileText className="me-1" size={14} />
                    PDF, DOC, DOCX, JPG, PNG
                  </span>
                  <span>
                    <Icon.HddStack className="me-1" size={14} />
                    Max size: 10MB
                  </span>
                </div>
              </Form.Text>

              {reportFile && (
                <div className="alert alert-success border-0 mt-3 mb-0 d-flex align-items-start">
                  <Icon.FileEarmarkCheck className="me-2 mt-1 flex-shrink-0 text-success" size={20} />
                  <div className="flex-grow-1 min-w-0">
                    <strong className="d-block mb-1">File Selected</strong>
                    <div className="text-truncate small mb-1">{reportFile.name}</div>
                    <small className="text-muted">
                      Size: {(reportFile.size / 1024).toFixed(2)} KB
                    </small>
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 px-3 px-sm-4 pb-3 pb-sm-4">
          <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2 w-100">


            {uploading ? (
              <Button
                variant="primary"
                disabled
                className="order-1 order-sm-2"
                style={{ minWidth: '140px' }}
              >
                <span className="spinner-border spinner-border-sm me-2" />
                Uploading...
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={(e) => {
                  handleReportUpload();
                  e.currentTarget.blur();
                }}
                className="order-1 order-sm-2"
                style={{ minWidth: '140px' }}
              >
                <Icon.CloudUpload className="me-2" size={18} />
                {existingReport ? "Update Report" : "Upload Report"}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3 d-none d-sm-flex">
              <Icon.ClipboardCheck size={22} className="text-primary" />
            </div>
            <span className="fs-5">Update Appointment Status</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-3 px-sm-4">
          {selectedAppointment && (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-3">
                <div className="row g-3">
                  <div className="col-md-6">
                    <small className="text-muted d-block mb-1">Client</small>
                    <strong>{selectedAppointment.appoint_clientname}</strong>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted d-block mb-1">Staff</small>
                    <strong>{selectedAppointment.appoint_staffname}</strong>
                  </div>
                  <div className="col-12">
                    <small className="text-muted d-block mb-1">Appointment Date</small>
                    <strong>
                      {new Date(selectedAppointment.appoint_appointmentdate).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Appointment Status</Form.Label>
              <Form.Select
                value={statusForm.appoint_status}
                onChange={(e) => handleStatusFormChange("appoint_status", Number(e.target.value))}
              >
                {appointmentStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Appointment Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={statusForm.appoint_notes}
                onChange={(e) => handleStatusFormChange("appoint_notes", e.target.value)}
                placeholder="Enter appointment notes"
              />
            </Form.Group>

            {Number(statusForm.appoint_status) === 3 && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Appointment Report</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => handleStatusFormChange("appoint_report", e.target.files?.[0] || null)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Follow up required"
                    checked={statusForm.appoint_followup}
                    onChange={(e) => handleStatusFormChange("appoint_followup", e.target.checked)}
                  />
                </Form.Group>

                {statusForm.appoint_followup && (
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Form.Label className="fw-semibold">Follow-up Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={statusForm.appoint_followupdate}
                        onChange={(e) => handleStatusFormChange("appoint_followupdate", e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label className="fw-semibold">From Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={statusForm.appoint_followuptimefrom}
                        onChange={(e) => handleStatusFormChange("appoint_followuptimefrom", e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label className="fw-semibold">To Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={statusForm.appoint_followuptimeto}
                        onChange={(e) => handleStatusFormChange("appoint_followuptimeto", e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {Number(statusForm.appoint_status) === 4 && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Cancellation / Rejection Report</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusForm.appointrej_report}
                  onChange={(e) => handleStatusFormChange("appointrej_report", e.target.value)}
                  placeholder="Enter cancellation or rejection details"
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 px-3 px-sm-4 pb-4">
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={submitStatusUpdate} disabled={statusSubmitting}>
            {statusSubmitting ? "Saving..." : "Save Status"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showApprovalModal}
        onHide={() => { setShowApprovalModal(false); setRejectionNote(""); }}
        centered
        size="md"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                <Icon.ClipboardCheck size={28} className="text-primary" />
              </div>
              <div>
                <h5 className="mb-0 fw-bold">Appointment Approval</h5>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-3 px-sm-4 py-3">
          {approvalData && (
            <>
              {/* Appointment Information Card */}
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-3">
                  <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <Icon.InfoCircle className="me-2" size={14} />
                    Appointment Details
                  </h6>

                  <table className="table table-borderless mb-0" style={{ fontSize: '0.95rem' }}>
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: '40%', paddingLeft: 0 }}>
                          <Icon.PersonBadge className="me-2 text-primary" size={16} />
                          Staff Member
                        </td>
                        <td style={{ paddingRight: 0 }}>
                          <strong>: {approvalData.appoint_staffname}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted" style={{ width: '40%', paddingLeft: 0 }}>
                          <Icon.Person className="me-2 text-primary" size={16} />
                          Client Name
                        </td>
                        <td style={{ paddingRight: 0 }}>
                          <strong>: {approvalData.appoint_clientname}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted" style={{ width: '40%', paddingLeft: 0 }}>
                          <Icon.CalendarCheck className="me-2 text-primary" size={16} />
                          Appointment Date
                        </td>
                        <td style={{ paddingRight: 0 }}>
                          <strong>
                            : {new Date(approvalData.appoint_appointmentdate).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted" style={{ width: '40%', paddingLeft: 0 }}>
                          <Icon.CircleFill
                            className={`me-2 ${approvalData.appoint_status === 0 ? 'text-warning' : 'text-success'}`}
                            size={10}
                          />
                          Status
                        </td>
                        <td style={{ paddingRight: 0 }}>
                          <strong>: </strong>
                          <span className={`badge ${approvalData.appoint_status === 0 ? 'bg-warning' : 'bg-success'}`}>
                            {approvalData.appoint_status === 0 ? "Pending" : "Completed"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Reports Section */}
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-3">
                  <h6 className="text-uppercase text-muted mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <Icon.FileEarmarkText className="me-2" size={14} />
                    Reports
                  </h6>

                  {/* Row 1 — Current / newly submitted report */}
                  <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded mb-2">
                    <div className="d-flex align-items-center">
                      <div className={`rounded-circle p-2 me-3 ${approvalData.report ? 'bg-success bg-opacity-10' : 'bg-secondary bg-opacity-10'}`}>
                        <Icon.FileEarmarkCheck
                          className={approvalData.report ? 'text-success' : 'text-secondary'}
                          size={18}
                        />
                      </div>
                      <div>
                        {approvalData.report ? (
                          <>
                            <strong className="d-block" style={{ fontSize: '0.9rem' }}>
                              {approvalData.rejected_report ? 'New Report' : 'Report Uploaded'}
                            </strong>

                          </>
                        ) : (
                          <>
                            <strong className="d-block text-muted" style={{ fontSize: '0.9rem' }}>No Report Uploaded</strong>
                            <small className="text-muted">Report has not been submitted yet</small>
                          </>
                        )}
                      </div>
                    </div>
                    {approvalData.report && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          viewReport(approvalData.appoint_id);
                        }}
                        className="d-flex align-items-center"
                      >
                        <Icon.Eye className="me-2" size={15} />
                        View Report
                      </Button>
                    )}
                  </div>


                  {approvalData.rejected_report && (
                    <div
                      className="d-flex align-items-start justify-content-between p-3 rounded"
                      style={{ background: '#fff5f5', border: '1px solid rgba(220,53,69,0.25)' }}
                    >
                      <div className="d-flex align-items-start me-3" style={{ minWidth: 0 }}>
                        <div className="rounded-circle p-2 me-3 bg-danger bg-opacity-10 flex-shrink-0">
                          <Icon.XCircleFill className="text-danger" size={18} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <strong className="d-block text-danger" style={{ fontSize: '0.88rem' }}>
                            Previously Rejected Report
                          </strong>
                          {approvalData.rejected_on && (
                            <small className="text-muted d-block">
                              Rejected on:{' '}
                              {new Date(approvalData.rejected_on).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric',
                              })}
                            </small>
                          )}
                          {approvalData.rejection_note && (
                            <div className="mt-1 p-2 rounded" style={{ background: 'rgba(220,53,69,0.07)', fontSize: '0.85rem' }}>
                              <Icon.ChatLeftText className="me-1 text-danger" size={12} />
                              <em className="text-dark">{approvalData.rejection_note}</em>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={(e) => {
                          e.currentTarget.blur();   
                          viewRejectedReport(approvalData.appoint_id); 
                        }}
                        className="d-flex align-items-center flex-shrink-0"
                      >
                        <Icon.Eye className="me-2" size={15} />
                        View Rejected
                      </Button>
                    </div>
                  )}

                </div>
              </div>

              {/* Manager Rejection Note Input — only visible to managers */}
              {isManager && (
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body p-3">
                    <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <Icon.ChatLeftText className="me-2" size={14} />
                      Rejection Note
                    </h6>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter reason for rejection..."
                      value={rejectionNote}
                      onChange={(e) => setRejectionNote(e.target.value)}
                      style={{ resize: 'none', fontSize: '0.9rem' }}
                    />
                    <small className="text-muted d-block mt-1">
                      This note will be visible to the staff member
                    </small>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        {isManager && (
          <Modal.Footer className="border-0 pt-0 px-3 px-sm-4 pb-4">
            <div className="d-flex flex-column-reverse flex-sm-row justify-content-end gap-2 w-100">
              <Button
                variant="danger"
                onClick={(e) => {
                  e.currentTarget.blur();
                  if (!rejectionNote.trim()) {
                    Swal.fire({
                      icon: "warning",
                      title: "Rejection Note Required",
                      text: "Please provide a reason for rejection so the staff can correct and resubmit.",
                      confirmButtonText: "OK",
                    });
                    return;
                  }
                  Swal.fire({
                    title: "Reject Appointment?",
                    text: "Are you sure you want to reject this appointment?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc3545",
                    cancelButtonColor: "#6c757d",
                    confirmButtonText: "Yes, reject it",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      axios.post(
                        baseurladdress + "appointment/rejectappointment",
                        {
                          appoint_id: approvalData.appoint_id,
                          roleid: localStorage.getItem("roleid"),
                          rejection_note: rejectionNote.trim(),
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${accesstoken}`,
                            "Content-Type": "application/json",
                          },
                        }
                      ).then(() => {
                        Swal.fire({
                          toast: true,
                          position: "top-end",
                          icon: "info",
                          title: "Appointment Rejected",
                          showConfirmButton: false,
                          timer: 2000,
                        });
                        setShowApprovalModal(false);
                        setRejectionNote("");
                        getAllAppointments();
                      });
                    }
                  });
                }}
                className="px-4"
              >
                <Icon.XCircle className="me-1" size={18} />
                Reject
              </Button>

              <Button
                variant="success"
                onClick={(e) => {
                  e.currentTarget.blur();
                  Swal.fire({
                    title: "Approve Appointment?",
                    text: "This will approve the appointment and notify relevant parties.",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#198754",
                    cancelButtonColor: "#6c757d",
                    confirmButtonText: "Yes, approve it",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      approveAppointment(approvalData.appoint_id);
                    }
                  });
                }}
                className="px-4"
              >
                <Icon.CheckCircle className="me-1" size={18} />
                Approve
              </Button>
            </div>
          </Modal.Footer>
        )}

      </Modal>

      {/* ── Report Viewer Modal ── */}
      <Modal
        show={showReportViewer}
        onHide={closeReportViewer}
        size="xl"
        centered
        backdrop="static"
        dialogClassName="report-viewer-dialog"
      >
        <Modal.Header className="border-0 pb-2 bg-dark text-white rounded-top">
          <Modal.Title className="d-flex align-items-center fs-6">
            <Icon.FileEarmarkText className="me-2" size={20} />
            Report Viewer
          </Modal.Title>
          <Button
            variant="outline-light"
            size="sm"
            onClick={closeReportViewer}
            className="ms-auto d-flex align-items-center gap-1"
          >
            <Icon.XLg size={14} />
            Close
          </Button>
        </Modal.Header>

        <Modal.Body className="p-0 bg-secondary bg-opacity-10" style={{ minHeight: '75vh' }}>
          {reportViewerLoading && (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '75vh' }}>
              <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
              <p className="text-muted">Loading report...</p>
            </div>
          )}

          {!reportViewerLoading && reportViewerUrl && (
            <>
              {/* PDF — rendered via PDF.js canvas with correct device pixel ratio */}
              {reportViewerType?.includes("pdf") && (
                <PdfViewer dataUrl={reportViewerUrl} />
              )}

              {/* Images */}
              {reportViewerType?.startsWith("image/") && (
                <div className="d-flex align-items-center justify-content-center p-4" style={{ minHeight: '75vh' }}>
                  <img
                    src={reportViewerUrl}
                    alt="Report"
                    style={{ maxWidth: '100%', maxHeight: '74vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}
                  />
                </div>
              )}

              {/* DOC / DOCX — can't be rendered inline, show message */}
              {(reportViewerType?.includes("msword") || reportViewerType?.includes("wordprocessingml")) && (
                <div className="d-flex flex-column align-items-center justify-content-center gap-3" style={{ height: '75vh' }}>
                  <Icon.FileEarmarkWord size={64} className="text-primary" />
                  <p className="text-muted mb-0">Word documents cannot be previewed directly.</p>
                  <a href={reportViewerUrl} download="report.docx" className="btn btn-primary btn-sm">
                    <Icon.Download className="me-2" size={16} />
                    Download to View
                  </a>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
}

/**
 * PdfViewer — renders PDF pages onto <canvas> elements using PDF.js.
 *
 * KEY FIX FOR MOBILE BLUR:
 * Mobile screens have a high devicePixelRatio (2x, 3x).
 * We render the canvas at (cssWidth × dpr) physical pixels,
 * then scale it back down with CSS — this gives crisp sharp text.
 */
function PdfViewer({ dataUrl }) {
  const containerRef = useRef(null);
  const [totalPages, setTotalPages] = useState(0);
  const [renderedPages, setRenderedPages] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dataUrl) return;
    let cancelled = false;

    async function renderPdf() {
      try {
        // Load PDF.js from CDN once
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        }

        const pdfjs = window.pdfjsLib;

        // Decode base64 → Uint8Array
        const base64 = dataUrl.split(",")[1];
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;

        setTotalPages(pdf.numPages);
        if (containerRef.current) containerRef.current.innerHTML = "";

        // devicePixelRatio: 1 on desktop, 2–3 on modern mobile screens
        const dpr = window.devicePixelRatio || 1;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;

          const page = await pdf.getPage(pageNum);

          // CSS width of the container
          const cssWidth = containerRef.current?.clientWidth || window.innerWidth - 24;

          // Scale to fill CSS width, then multiply by dpr for sharpness
          const baseViewport = page.getViewport({ scale: 1 });
          const cssScale = cssWidth / baseViewport.width;
          const renderScale = cssScale * dpr;
          const renderViewport = page.getViewport({ scale: renderScale });

          const canvas = document.createElement("canvas");
          // Physical pixels — large and sharp
          canvas.width = renderViewport.width;
          canvas.height = renderViewport.height;
          // CSS size — scaled back down so it fits the screen
          canvas.style.width = cssWidth + "px";
          canvas.style.height = (renderViewport.height / dpr) + "px";
          canvas.style.display = "block";
          canvas.style.marginBottom = pageNum < pdf.numPages ? "6px" : "0";

          if (containerRef.current) containerRef.current.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport: renderViewport,
          }).promise;

          if (!cancelled) setRenderedPages(pageNum);
        }
      } catch (err) {
        if (!cancelled) setError("Failed to render PDF.");
        console.error("PDF.js render error:", err);
      }
    }

    renderPdf();
    return () => { cancelled = true; };
  }, [dataUrl]);

  if (error) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 p-4" style={{ minHeight: "60vh" }}>
        <p className="text-danger mb-0">{error}</p>
      </div>
    );
  }

  return (
    <div style={{ background: "#525659", padding: "12px", minHeight: "75vh" }}>
      {totalPages > 0 && renderedPages < totalPages && (
        <div className="mb-2">
          <div className="progress" style={{ height: "3px" }}>
            <div
              className="progress-bar bg-primary"
              style={{ width: `${(renderedPages / totalPages) * 100}%`, transition: "width 0.3s" }}
            />
          </div>
          <small className="text-white-50 d-block text-center mt-1" style={{ fontSize: "0.72rem" }}>
            Page {renderedPages} of {totalPages}
          </small>
        </div>
      )}
      <div ref={containerRef} style={{ maxWidth: "860px", margin: "0 auto" }} />
    </div>
  );
}

export default Appointments;